import { useEffect, useRef } from 'react';
import axios from 'axios';
import { SplashBrandStack } from '../components/Logo';

export const AuthCallback = () => {
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id found');
          window.location.replace('/login');
          return;
        }

        // Exchange session_id for session_token - this sets the httpOnly cookie
        let userData = null;
        try {
          const response = await axios.post(
            `${window.__SG_API_BASE__}/api/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          userData = response.data;
        } catch (exchangeError) {
          // 403 → hard beta gate blocked this NEW account creation. Send to gate with a message.
          if (exchangeError?.response?.status === 403) {
            const msg = exchangeError?.response?.data?.detail || 'Private Beta access required.';
            // Display-only error string is non-sensitive. catch handles
            // sessionStorage being blocked (private mode).
            try { window.sessionStorage?.setItem('sg.gate_error', msg); } catch { /* storage blocked — accept failure */ }
            window.location.replace('/');
            return;
          }
          // session_id may have already been exchanged (browser refresh, double-mount, etc.)
          // Try to verify if we already have a valid session cookie
          console.warn('Session exchange failed, checking existing session:', exchangeError?.response?.status);
          try {
            const meResponse = await axios.get(
              `${window.__SG_API_BASE__}/api/auth/me`,
              { withCredentials: true }
            );
            userData = meResponse.data;
          } catch (meError) {
            console.error('No valid session found:', meError);
            window.location.replace('/login');
            return;
          }
        }

        if (!userData) {
          window.location.replace('/login');
          return;
        }

        // Use window.location.replace for hard navigation - this:
        // 1. Clears the URL hash containing session_id (prevents re-processing)
        // 2. Forces a fresh page load so AuthProvider's checkAuth runs cleanly
        // 3. Ensures the httpOnly cookie is sent on the new request
        // v3.1 — honor a stashed internal `next` destination (whitelisted, no open redirects).
        const ALLOWED_NEXT = ['/', '/home', '/closet', '/stylist', '/planner', '/style-academy',
          '/founder', '/settings', '/quiz', '/search', '/shop', '/dashboard'];
        let next = null;
        try {
          next = window.sessionStorage?.getItem('sg.auth_next');
          window.sessionStorage?.removeItem('sg.auth_next');
        } catch { /* storage blocked */ }
        const nextSafe = next && next.startsWith('/') && !next.startsWith('//')
          && ALLOWED_NEXT.some(p => next === p || next.startsWith(p + '/'));
        const targetPath = nextSafe ? next : (userData.quiz_completed ? '/dashboard' : '/quiz');
        window.location.replace(targetPath);
      } catch (error) {
        console.error('Auth callback error:', error);
        // Stash a polished message the landing page can surface on next load.
        try {
          window.sessionStorage?.setItem(
            'sg.gate_error',
            'We could not complete sign-in. Please try again.'
          );
        } catch { /* storage blocked — accept failure */ }
        window.location.replace('/login');
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
      <div className="text-center">
        <div className="mb-8"><SplashBrandStack /></div>
        <div className="w-8 h-8 border-2 border-[#C7A96B] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#F7F5F1]/70 font-light tracking-wide text-sm">Completing sign in…</p>
      </div>
    </div>
  );
};
